/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { DropDownComponentProps, RegionsType } from './DropDownComponent.types';
import { StyledDropDownComponent } from './DropDownComponent.styles';
import { InputTextField, DropDownModal, IconTextBlock } from '@marriott/mi-ui-library';
import { getDescriptionByCode } from '../../modules/helpers';

export const DropDownComponent: FC<DropDownComponentProps> = ({
  regions,
  onClick,
  defaultValue = '',
  clearButtonLabel = '',
  clearClickHandler,
  viewPort,
  textFieldLabel,
}) => {
  const defaultPopUpOpen = viewPort === 'viewPortL' ? true : false;

  const [isOpen, setIsOpen] = useState(defaultPopUpOpen);
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredRegions, setFilteredRegions] = useState(regions);

  const inputElementRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const optionSelected = (region: { description: string }, type?: string) => {
    onClick(region, type);
    setInputValue(region?.description);
    !defaultPopUpOpen && setIsOpen(false);
  };

  const inputFocus = () => {
    inputElementRef.current?.focus();
  };

  const handleDropDownBlur = (event: any) => {
    if (!dropdownRef.current?.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const lowercasedValue = value?.toLowerCase();

    if (value) {
      const filteredData = filteredRegions
        ?.map((region: { buckets: RegionsType[] }) => {
          const filteredBuckets = region?.buckets?.filter(
            (bucket: RegionsType) =>
              bucket?.label?.toLowerCase()?.startsWith(lowercasedValue) ||
              bucket?.code?.toLowerCase()?.startsWith(lowercasedValue)
          );
          return { ...region, buckets: filteredBuckets };
        })
        ?.filter((region: { buckets: RegionsType[] }) => region?.buckets?.length > 0);
      setFilteredRegions(filteredData);
    } else {
      setFilteredRegions(regions);
    }
  };

  useEffect(() => {
    setFilteredRegions(regions);
    const iValue = regions ? getDescriptionByCode(regions, defaultValue) : '';
    setInputValue(iValue);
  }, [regions]);

  return (
    <StyledDropDownComponent
      data-testid="luxury-DropDownComponent"
      data-component-name="m-luxury-DropDownComponent"
      ref={dropdownRef}
      className="mb-3 color-scheme1"
      onFocus={() => !defaultPopUpOpen && setIsOpen(true)}
    >
      <>
        {viewPort !== 'viewPortL' && <div className="t-subtitle-l pb-1 mb-2">{textFieldLabel}</div>}
        {!defaultPopUpOpen && (
          <InputTextField
            className={clsx('m-input-field t-font-m mb-3')}
            togglePlaceholder={true}
            inputTextFieldClassName="dropdown-input-field"
            isClickEnabled={true}
            inputValue={inputValue}
            placeHolderText={'Select an Option'}
            showIcon={true}
            iconClass={clsx('custom_click_track', isOpen ? 'icon-arrow-up' : 'icon-arrow-down')}
            iconForLabel={isOpen ? 'icon-arrow-up' : 'icon-arrow-down'}
            iconOnClick={inputFocus}
            getInputValue={handleInputChange}
            handleKeyDown={inputFocus}
            onBlur={handleDropDownBlur}
            iconAriaLabel={'iconAriaLabel'}
            ref={inputElementRef}
            customDownshiftLabel={true}
            infoLabel={textFieldLabel}
          />
        )}

        {isOpen && (
          <DropDownModal
            dropDownChildrenClassName="col-12 p-0"
            isAutoScrollEnabled={true}
            dropDownSpecificFlag={false}
            scrollDownModalCloseState={false}
            labelledBy="luxury-dropdown"
            show={true}
            className={`dropdown-container`}
            childrenClassName={'luxury-dropdown-main'}
            dropdownModalOpenState={isOpen}
            setDropdownModalOpenState={setIsOpen}
            clearButtonLabel={clearButtonLabel}
            desktopDropdown={defaultPopUpOpen ? true : false}
            mobileFooterEnabled={defaultPopUpOpen ? true : false}
            clearClickHandler={clearClickHandler}
          >
            {defaultPopUpOpen && (
              <InputTextField
                className={clsx('m-input-text-field t-font-m', 'm-3')}
                togglePlaceholder={true}
                inputTextFieldClassName="dropdown-input-field"
                isClickEnabled={true}
                inputValue={inputValue}
                placeHolderText={'Select an Option'}
                showIcon={false}
                getInputValue={handleInputChange}
                handleKeyDown={inputFocus}
                iconAriaLabel={'iconAriaLabel'}
                ref={inputElementRef}
                label={textFieldLabel}
                customDownshiftLabel={true}
                infoLabel={textFieldLabel}
              />
            )}
            <ul className="p-0" role="list" aria-label="list">
              {filteredRegions?.flatMap(
                (region: { buckets?: RegionsType[]; type?: string; code?: string; count?: number }) => {
                  return region?.buckets?.map((bucket: RegionsType) => (
                    <li
                      key={bucket?.code}
                      className={clsx('region-el', 'mx-2', {
                        active: defaultValue && bucket?.code === defaultValue,
                      })}
                      onClick={() => optionSelected(bucket, region?.type)}
                      onKeyDown={() => optionSelected(bucket, region?.type)}
                      tabIndex={0}
                      role="listitem"
                    >
                      <IconTextBlock
                        icon="location"
                        primaryCustomClass="region-text"
                        primaryTextContent={`${bucket?.description}`}
                        secondaryTextContent={`${bucket?.count}`}
                      />
                    </li>
                  ));
                }
              )}
            </ul>
          </DropDownModal>
        )}
      </>
    </StyledDropDownComponent>
  );
};
